import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserProfile } from '../contexts/UserProfileContext';
import Header from '../components/Header';
import jsPDF from 'jspdf';
import { ArrowLeft, Printer, FileText, Stethoscope, ChevronDown, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import lumosyeLogo from '../assets/Lumosye-logo.png';
import lumochartLogo from '../assets/lumochart-login.png';


// --- Import Calculator Components ---
import InsulinCalculatorSuite from './InsulinCalculatorSuite';
import OpioidConverter from './OpioidConverter';
import SteroidTaperCalculator from './SteroidTaperCalculator';

// --- Reusable Components (moved here to be self-contained) ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
};

const FormInput = ({ label, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
        <input 
            {...props}
            className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
        />
    </div>
);

const FormTextarea = ({ label, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
        <textarea 
            {...props}
            className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
        />
    </div>
);

const ToggleSwitch = ({ label, checked, onChange }) => (
    <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        <button type="button" onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-zinc-700'}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);

const DrugSearchInput = ({ label, onSelectDrug }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        if (debouncedSearchTerm.length > 2) {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            fetch(`${backendUrl}/search-drugs?query=${encodeURIComponent(debouncedSearchTerm)}`)
                .then(res => res.json())
                .then(data => setResults(data.results || []))
                .catch(error => console.error("Drug search failed:", error));
        } else {
            setResults([]);
        }
    }, [debouncedSearchTerm]);

    const handleSelect = (drugName) => {
        setSearchTerm(drugName);
        onSelectDrug(drugName);
        setResults([]);
    };

    return (
        <div className="relative">
            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input 
                    type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Start typing a drug name..."
                    className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg pl-10 pr-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                />
            </div>
            {results.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {results.map(drug => (
                        <li key={drug.id} onClick={() => handleSelect(drug.name)} className="px-4 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-zinc-700">
                            {drug.name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border-t border-gray-200 dark:border-zinc-700 first:border-t-0">
            <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center py-4 text-xl font-bold text-gray-800 dark:text-white">
                {title}
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }}><ChevronDown /></motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="pb-6">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default function DispositionTools() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { profile } = useUserProfile();
  const [patient, setPatient] = useState(null);
  const [activeTab, setActiveTab] = useState('Prescription');
  
  const [rxData, setRxData] = useState({ drugName: '', sig: '', quantity: '', refills: '', notesToPharmacy: '' });
  const [includeDEA, setIncludeDEA] = useState(false);
  const [letterContent, setLetterContent] = useState('');

  useEffect(() => {
    const fetchPatient = async () => {
      if (!patientId) return;
      const patientRef = doc(db, 'patients', patientId);
      const patientSnap = await getDoc(patientRef);
      if (patientSnap.exists()) setPatient(patientSnap.data());
    };
    fetchPatient();
  }, [patientId]);
  
  const handleRxChange = (e) => setRxData(prev => ({...prev, [e.target.name]: e.target.value}));
  
  const generatePrescriptionPDF = () => {
      if (!patient || !profile) return alert("Patient or prescriber profile not loaded.");
      const docPDF = new jsPDF('p', 'pt', 'letter');
      const page = { width: docPDF.internal.pageSize.getWidth(), height: docPDF.internal.pageSize.getHeight() };
      const margin = 40;
      let cursorY = margin;

      docPDF.setFont("helvetica", "bold").setFontSize(16);
      docPDF.text(profile.fullName || '', margin, cursorY);
      const dateString = `Date: ${new Date().toLocaleDateString()}`;
      docPDF.setFont("helvetica", "normal").setFontSize(12);
      docPDF.text(dateString, page.width - margin, cursorY, { align: 'right' });
      cursorY += 15;
      docPDF.text(profile.address || '', margin, cursorY);
      cursorY += 15;
      docPDF.text(`Phone: ${profile.phone || ''}`, margin, cursorY);
      cursorY += 25;
      docPDF.setDrawColor(220).line(margin, cursorY, page.width - margin, cursorY);
      cursorY += 25;

      docPDF.setFont("helvetica", "bold").setFontSize(12);
      docPDF.text("For Patient:", margin, cursorY); cursorY += 15;
      docPDF.text(`${patient.firstName} ${patient.lastName}`, margin + 10, cursorY); cursorY += 15;
      const dob = patient.dob ? new Date(patient.dob.seconds * 1000).toLocaleDateString() : 'N/A';
      docPDF.text(`DOB: ${dob}`, margin + 10, cursorY); cursorY += 60;

      docPDF.setFont("helvetica", "bold").setFontSize(48).text("Rx", margin, cursorY); cursorY += 10;
      docPDF.setFont("helvetica", "bold").setFontSize(16);
      const drugLines = docPDF.splitTextToSize(rxData.drugName, page.width - margin*2 - 20);
      docPDF.text(drugLines, margin + 20, cursorY); cursorY += drugLines.length * 18;
      docPDF.setFont("helvetica", "normal").setFontSize(14);
      const sigLines = docPDF.splitTextToSize(rxData.sig, page.width - margin*2 - 20);
      docPDF.text(sigLines, margin + 20, cursorY); cursorY += sigLines.length * 16 + 14;
      docPDF.setFontSize(12);
      docPDF.text(`Quantity: ${rxData.quantity}`, margin + 20, cursorY);
      docPDF.text(`Refills: ${rxData.refills}`, margin + 200, cursorY);
      if (rxData.notesToPharmacy) { cursorY += 20; docPDF.text(`Notes: ${rxData.notesToPharmacy}`, margin + 20, cursorY); }

      const signatureY = page.height - margin - 60;
      docPDF.setFont("helvetica", "normal").setFontSize(10);
      docPDF.text(`NPI: ${profile.npiNumber || ''}`, margin, signatureY);
      if (includeDEA && profile.deaNumber) {
          docPDF.text(`DEA: ${profile.deaNumber || ''}`, margin, signatureY + 12);
      }
      const signatureText = `Signature: ${profile.fullName}, ${profile.title}`;
      docPDF.setDrawColor(0).line(page.width - margin - 250, signatureY - 2, page.width - margin, signatureY - 2);
      docPDF.text(signatureText, page.width - margin, signatureY + 10, { align: 'right' });
      
      docPDF.save(`Prescription_${patient.lastName}.pdf`);
  };

  const generateLetterPDF = () => {
      if (!patient || !profile) return alert("Patient or prescriber profile not loaded.");
      const docPDF = new jsPDF('p', 'pt', 'letter');
      const page = { width: docPDF.internal.pageSize.getWidth(), height: docPDF.internal.pageSize.getHeight() };
      const margin = 50;
      let cursorY = margin;

      const lumosyeRatio = 3226 / 825;
      const lumosyeWidth = 120;
      const lumosyeHeight = lumosyeWidth / lumosyeRatio;
      docPDF.addImage(lumosyeLogo, 'PNG', margin, cursorY, lumosyeWidth, lumosyeHeight);
      const lumochartSize = 50;
      docPDF.addImage(lumochartLogo, 'PNG', page.width - margin - lumochartSize, cursorY, lumochartSize, lumochartSize);
      cursorY += Math.max(lumosyeHeight, lumochartSize) + 20;
      docPDF.setDrawColor(220).line(margin, cursorY, page.width - margin, cursorY);
      cursorY += 40;
      
      docPDF.setFont("times", "normal").setFontSize(12);
      const lines = docPDF.splitTextToSize(letterContent, page.width - (margin * 2));
      docPDF.text(lines, margin, cursorY);
      
      const pageCount = docPDF.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
          docPDF.setPage(i);
          docPDF.setFontSize(9).setTextColor(150);
          const addressText = (profile.address || '').replace(/\n/g, ', ');
          docPDF.text(addressText, margin, page.height - 20);
          docPDF.text(`Page ${i} of ${pageCount}`, page.width - margin, page.height - 20, { align: 'right' });
      }
      
      docPDF.save(`Letter_${patient.lastName}.pdf`);
  };
  
  const applyTemplate = (type) => {
      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const patientName = `${patient?.firstName || ''} ${patient?.lastName || ''}`.trim();
      const prescriberName = `${profile?.fullName || ''}, ${profile?.title || ''}`;

      if (type === 'excuse') {
          setLetterContent(`Date: ${today}\n\nTo Whom It May Concern,\n\nPlease excuse ${patientName} from work/school on ${today} due to a medical evaluation.\n\nSincerely,\n\n${prescriberName}`);
      } else if (type === 'return') {
          setLetterContent(`Date: ${today}\n\nTo Whom It May Concern,\n\n${patientName} has been under my care and is cleared to return to work/school without restrictions, effective immediately.\n\nSincerely,\n\n${prescriberName}`);
      }
      setActiveTab('Letterhead');
  };

  return (
    <div className="bg-[#f4f7f9] dark:bg-black min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto py-10 px-4">
        <div className="flex items-center mb-8">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-zinc-800 mr-4">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">Disposition Tools</h1>
            <p className="text-gray-500 dark:text-gray-400">
              for {patient?.firstName} {patient?.lastName}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl p-8">
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-2 bg-slate-200/70 dark:bg-zinc-800 rounded-full p-1">
              {['Prescription', 'Letterhead', 'Templates', 'Calculators'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="relative px-5 py-1.5 text-sm font-semibold transition-colors duration-300"
                >
                  <span className={`relative z-10 ${activeTab === tab ? 'text-gray-800 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'}`}>
                    {tab}
                  </span>
                  {activeTab === tab && (
                    <motion.div
                      layoutId="active-tool-pill"
                      className="absolute inset-0 bg-white dark:bg-zinc-700 rounded-full shadow-sm"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'Prescription' && <div className="space-y-4">
              <DrugSearchInput label="Drug Name" onSelectDrug={(name) => setRxData(prev => ({...prev, drugName: name}))} />
              <FormInput label="Sig (Instructions)" name="sig" value={rxData.sig} onChange={handleRxChange} />
              <div className="grid grid-cols-2 gap-4">
                  <FormInput label="Quantity" name="quantity" value={rxData.quantity} onChange={handleRxChange} />
                  <FormInput label="Refills" name="refills" value={rxData.refills} onChange={handleRxChange} />
              </div>
              <FormInput label="Notes to Pharmacy (optional)" name="notesToPharmacy" value={rxData.notesToPharmacy} onChange={handleRxChange} />
              <div className="pt-2">
                <ToggleSwitch label="Include DEA Number on Rx" checked={includeDEA} onChange={setIncludeDEA} />
              </div>
              <button onClick={generatePrescriptionPDF} className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition"><Printer size={16} /> Export Prescription PDF</button>
          </div>}

          {activeTab === 'Letterhead' && <div className="space-y-4">
              <FormTextarea label="Letter Content" value={letterContent} onChange={(e) => setLetterContent(e.target.value)} rows={15} />
              <button onClick={generateLetterPDF} className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition"><Printer size={16} /> Export Letter PDF</button>
          </div>}
          
          {activeTab === 'Templates' && <div className="space-y-4 text-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Letter Templates</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Select a template to start a letter.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <button onClick={() => applyTemplate('excuse')} className="p-6 bg-slate-100 dark:bg-zinc-800 rounded-lg font-semibold hover:bg-slate-200 dark:hover:bg-zinc-700 transition"><FileText size={20} className="mx-auto mb-2"/> Excuse from Work/School</button>
                  <button onClick={() => applyTemplate('return')} className="p-6 bg-slate-100 dark:bg-zinc-800 rounded-lg font-semibold hover:bg-slate-200 dark:hover:bg-zinc-700 transition"><Stethoscope size={20} className="mx-auto mb-2"/> Return to Work/School</button>
              </div>
          </div>}

          {activeTab === 'Calculators' && (
            <div className="space-y-6">
              <CollapsibleSection title="Insulin Suite" defaultOpen>
                  <InsulinCalculatorSuite patient={patient} />
              </CollapsibleSection>
              <CollapsibleSection title="Opioid Converter">
                  <OpioidConverter />
              </CollapsibleSection>
              <CollapsibleSection title="Steroid Taper Generator">
                  <SteroidTaperCalculator />
              </CollapsibleSection>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}